import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { SegmentInput } from 'Components/contracts/segment'
import { useGetAllInquiriesContractsQuery } from 'Services/contracts/inquiriesContracts'
import { Contract } from 'Services/contracts/contractsUtils'
import { useGetSegmentsForContractQuery } from 'Services/segments/segments'
import {
  setContract,
  setNoOfLeads,
  setSegmentName,
  setSegmentsForContract,
  setSegmentsLeadsTotal,
  selectNewAnnualSegment,
} from './NewAnnualSegmentSlice'
import { buildFilterQuery } from 'Filter/buildFilterQuery'
import { CombineBy, FilterBy, properties } from 'Filter/properties'
import { Loading } from 'Utils/loading'
import { ContractSource, ContractStatus } from 'Types/index'

const pendingInProgressContractsQuery = () => ({
  query: {
    filterQuery: properties(
      [
        {
          column: 'source',
          op: FilterBy.EQ,
          value: ContractSource.inquiries,
        },
        {
          column: 'status',
          op: FilterBy.IN,
          value: [ContractStatus.pending, ContractStatus.in_progress],
        },
      ],
      CombineBy.AND
    ),
  },
})

interface AddAnnualProps {
  control: any
  setFormError: any
  clearFormErrors: any
  showSidebar: boolean
}

export const AddAnnual = ({
  control,
  setFormError,
  clearFormErrors,
  showSidebar,
}: AddAnnualProps) => {
  const dispatch = useDispatch()
  const { ipedId } = useParams()
  const {
    contract,
    segmentsRelatedToContract,
    segmentsLeadsTotal,
    noOfLeads,
    segmentName,
  } = useSelector(selectNewAnnualSegment)

  const { data: relatedContracts, isLoading: isLoadingContracts } =
    useGetAllInquiriesContractsQuery(
      {
        ipedId,
        filterQuery: buildFilterQuery(pendingInProgressContractsQuery()),
      },
      { skip: showSidebar === false }
    )

  let segmentNames = []
  let segmentLeadsTotal = 0
  const remainingLeads = contract?.lead_cap - segmentsLeadsTotal

  const { data: segments, isLoading: isLoadingSegments } =
    useGetSegmentsForContractQuery(
      {
        ipedId,
        contractId: contract?.id,
      },
      { skip: contract?.id == undefined }
    )

  const setSelectedContract = (contract: Contract) => {
    dispatch(setContract(contract))
  }

  const handleNoOfLeadsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value
    if (input !== '') {
      if (!isNaN(Number(input))) dispatch(setNoOfLeads(Number(input)))
    } else {
      dispatch(setNoOfLeads(''))
    }
  }

  const handleSegmentNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSegmentName(e.target.value))
  }

  /** Check if the leads is more than the available
   * in the contract */
  useEffect(() => {
    if (!isNaN(noOfLeads)) {
      if (remainingLeads - noOfLeads < 0) {
        setFormError(
          'root.noOfLeads',
          { type: 'manual', message: '' },
          { shouldFocus: true }
        )
      } else {
        clearFormErrors('root.noOfLeads')
      }
    }
  }, [noOfLeads, contract, segmentsLeadsTotal])

  /** Check and add duplicate segment name error */
  useEffect(() => {
    if (segmentsRelatedToContract.includes(segmentName)) {
      setFormError(
        'root.segmentName',
        {
          type: 'manual',
          message: 'This name is already in use on the same Contract',
        },

        { shouldFocus: true }
      )
    } else {
      clearFormErrors('root.segmentName')
    }
  }, [segmentName])

  /** Calculate the related segments and the total leads for a contract */
  useEffect(() => {
    if (segments && contract) {
      segments?.forEach((segment: any) => {
        segmentNames.push(segment.name)
        segmentLeadsTotal += segment.is_active
          ? segment.lead_total
          : segment.leads_delivered ?? 0
      })
      clearFormErrors('root.relatedContracts')
      dispatch(setSegmentsForContract(segmentNames))
      dispatch(setSegmentsLeadsTotal(segmentLeadsTotal))
    }
  }, [segments, contract])

  if (isLoadingContracts) return <Loading />

  return (
    <SegmentInput
      setSelectedContract={setSelectedContract}
      handleNoOfLeadsChange={handleNoOfLeadsChange}
      handleSegmentNameChange={handleSegmentNameChange}
      relatedContracts={relatedContracts}
      chosenContract={contract}
      noOfLeads={noOfLeads}
      segmentName={segmentName}
      remainingLeads={remainingLeads}
      control={control}
      source={ContractSource.inquiries}
    />
  )
}
